import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  RoleAssignmentWithRelationsDto,
  ResponseDto,
  CreateRoleAssignmentDto,
} from "@mimber/schema"
import { roleAssignmentsKeys, rolesKeys } from "../roles.utils"

/**
 * Organization-specific hook for assigning a role to a member
 * Can only be used in mimber-organization app
 */
export const useAssignRole = (
  roleUuid: string,
  options?: UseMutationOptions<
    ResponseDto<RoleAssignmentWithRelationsDto>,
    Error,
    CreateRoleAssignmentDto
  >
): UseMutationResult<
  ResponseDto<RoleAssignmentWithRelationsDto>,
  Error,
  CreateRoleAssignmentDto
> => {
  guardAppUsage("mimber-organization", "useAssignRole")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (dto: CreateRoleAssignmentDto) => {
      const response = await server.post<
        ResponseDto<RoleAssignmentWithRelationsDto>
      >(`roles/${roleUuid}/assignments`, dto)
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: roleAssignmentsKeys.findByRole(roleUuid),
      })
      queryClient.invalidateQueries({
        queryKey: rolesKeys.findOne(roleUuid),
      })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
